import { Panel, RmmSkeleton, SectionCard, StatCard } from '@components';
import { useCompany } from '@hooks';
import { RmmApp } from '@interfaces';
import { AssetsService } from '@services';
import { DateUtil, FormatNumbers, ListComponent } from '@utils';
import { Divider, Spin, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Cpu,
  Database,
  Globe,
  HardDrive,
  Hash,
  Layers,
  Monitor,
  Package,
  RefreshCw,
  Server,
  Shield,
  Terminal,
  User,
  Wifi,
  XCircle,
  Zap,
} from 'react-feather';
import ProductStorage from '../../hardware/product/ProductStorage';

interface Props {
  data: { node: any };
}

const statusColor = (state: string) =>
  state === 'Running' ? 'success' : state === 'Stopped' ? 'error' : 'warning';

// ─── Sub-components ───────────────────────────────────────────────────────────

const KVRow = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) => (
  <div className="flex justify-between items-center py-0.5 gap-2">
    <div className="flex items-center gap-1.5 text-sm text-slate-500 shrink-0">
      {icon && <span className="opacity-60">{icon}</span>}
      {label}
    </div>
    <div className="text-sm font-medium text-slate-800 dark:text-slate-200 text-right min-w-0 truncate">
      {value}
    </div>
  </div>
);

const IpChip = ({ ip }: { ip: string }) => (
  <span className="font-mono text-[11px] bg-slate-50 dark:bg-slate-700 border border-slate-100 dark:border-slate-600 rounded-md px-2 py-0.5 text-slate-700 dark:text-slate-300">
    {ip}
  </span>
);

// ─── RMM online badge ─────────────────────────────────────────────────────────

const RmmStatusBadge = ({ offline }: { offline: boolean }) =>
  offline ? (
    <Tag color="error" className="ml-auto flex-shrink-0">
      <XCircle size={10} className="inline mr-1" />
      Offline
    </Tag>
  ) : (
    <Tag color="success" className="ml-auto flex-shrink-0">
      <CheckCircle size={10} className="inline mr-1" />
      Online
    </Tag>
  );

function groupAppsByPublisher(apps: RmmApp[]): Record<string, RmmApp[]> {
  return apps.reduce<Record<string, RmmApp[]>>((acc, app) => {
    const key = app.publisher || 'Unknown';

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(app);

    return acc;
  }, {});
}

function groupAppsWithCount(apps: RmmApp[]) {
  const grouped = groupAppsByPublisher(apps);

  return Object.entries(grouped).map(([publisher, apps]) => ({
    publisher,
    count: apps.length,
    apps,
  }));
}

// ─── Main component ───────────────────────────────────────────────────────────

export const ServerDetailContainer = ({ data }: Props) => {
  const node = data?.node;
  const { kind } = node?.data ?? {};
  const serial = node?.data?.vm?.serialNumber;
  const { companyId } = useCompany();
  const [rmmDevice, setRmmDevice] = useState<RmmApp[]>([]);
  const [rmmApps, setRmmApps] = useState<any>(null);
  const [isLoadingRmm, setIsLoadingRmm] = useState(false);
  const assetService = new AssetsService();

  useEffect(() => {
    const fetchRmmData = async () => {
      if (!serial || !companyId) return;

      setIsLoadingRmm(true);

      try {
        const device = await assetService.getBySerialOnRmm(serial, companyId);
        setRmmDevice(device);
        const apps = await assetService.fetchNinjaApps(
          companyId,
          device.id,
          device.organizationId
        );
        setRmmApps(groupAppsWithCount(apps));
      } catch (error) {
        console.error('Error fetching RMM device/apps:', error);
      } finally {
        setIsLoadingRmm(false);
      }
    };

    fetchRmmData();
  }, [serial]);

  if (!node) {
    return <div className="py-4 text-sm text-slate-500">No data</div>;
  }

  // ─── Host View ───────────────────────────────────────────────────────────────
  if (kind === 'host') {
    const { host, updatedAt } = node.data;

    return (
      <div className="py-4 space-y-3">
        <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-700/60 px-4 py-3.5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center flex-shrink-0">
            <Server size={18} className="text-blue-500 dark:text-blue-400" />
          </div>
          <div>
            <div className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">
              {host.name}
            </div>
            <div className="text-xs text-slate-500">{host.fqdn}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">{host.os}</div>
          </div>
          <Tag color="success" className="ml-auto">
            Online
          </Tag>
        </div>

        <div className="flex gap-2">
          <StatCard
            label="CPU cores"
            value={host.cpu.cores}
            icon={<Cpu size={12} />}
          />
          <StatCard
            label="Memory"
            value={`${host.memoryGB} GB`}
            icon={<Database size={12} />}
          />
          <StatCard
            label="VMs"
            value={host.vms.length}
            icon={<Monitor size={12} />}
          />
        </div>

        <SectionCard title="Processor" icon={<Cpu size={13} />}>
          <KVRow
            label="Model"
            value={host.cpu.model}
            icon={<Layers size={12} />}
          />
          <KVRow
            label="Cores"
            value={host.cpu.cores}
            icon={<Zap size={12} />}
          />
        </SectionCard>

        <SectionCard title="Network" icon={<Wifi size={13} />}>
          <KVRow
            label="Gateway"
            value={host.network.gateway}
            icon={<Globe size={12} />}
          />
          <Divider className="my-2" />
          <div className="text-xs text-slate-500 mb-1.5">IP Addresses</div>
          <div className="flex flex-wrap gap-1">
            {host.network.ipAddresses.map((ip: string) => (
              <IpChip key={ip} ip={ip} />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title={`Virtual machines (${host.vms.length})`}
          icon={<Monitor size={13} />}
        >
          <div className="space-y-2">
            {host.vms.map((vm: any) => (
              <div
                key={vm.id}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-100 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <Monitor
                    size={14}
                    className="text-slate-300 dark:text-slate-600"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                      {vm.name}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {vm.cpuCount} vCPU · {Math.round(vm.memoryMB / 1024)} GB
                    </div>
                  </div>
                </div>
                <Tag color={statusColor(vm.state)}>{vm.state}</Tag>
              </div>
            ))}
          </div>
        </SectionCard>

        {updatedAt && (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 pb-2">
            <RefreshCw size={10} />
            Last updated {new Date(updatedAt).toLocaleString()}
          </div>
        )}
      </div>
    );
  }

  // ─── VM View ─────────────────────────────────────────────────────────────────
  if (kind === 'vm') {
    const { vm, hostName } = node.data;

    const rmm = rmmDevice;
    const rmmOs = rmm?.os;
    const rmmSystem = rmm?.system;
    const rmmProcessors: any[] = rmm?.processors ?? [];
    const rmmVolumes: any[] = rmm?.volumes ?? [];
    const rmmIPs: string[] = rmm?.ipAddresses ?? [];
    const rmmMacs: string[] = rmm?.macAddresses ?? [];

    return (
      <div className="py-4 space-y-3">
        {/* Header */}
        <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-700/60 px-4 py-3.5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center flex-shrink-0">
            <Monitor size={18} className="text-blue-500 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-slate-900 dark:text-slate-100 truncate">
              {rmm?.systemName ?? vm.name}
            </div>
            <div className="text-xs text-slate-500">
              {rmm?.dnsName ?? `Hosted on ${hostName}`}
            </div>
            {rmmOs?.name && (
              <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                {rmmOs.name}
              </div>
            )}
          </div>
          {isLoadingRmm ? (
            <div className="ml-auto flex-shrink-0">
              <Spin size="small" />
            </div>
          ) : rmm ? (
            <RmmStatusBadge offline={!!rmm.offline} />
          ) : (
            <Tag
              color={statusColor(vm.state)}
              className="ml-auto flex-shrink-0"
            >
              {vm.state}
            </Tag>
          )}
        </div>

        {/* Stat row */}
        <div className="flex gap-2">
          <StatCard
            label="vCPU"
            value={rmmProcessors[0]?.numLogicalCores ?? vm.cpuCount}
            icon={<Cpu size={12} />}
            sub={
              rmmProcessors[0]?.numCores
                ? `${rmmProcessors[0].numCores} physical`
                : undefined
            }
          />
          <StatCard
            label="Memory"
            value={
              rmmSystem?.totalPhysicalMemory
                ? FormatNumbers.formatBytes(rmmSystem.totalPhysicalMemory, 0)
                : `${Math.round(vm.memoryMB / 1024)} GB`
            }
            icon={<Database size={12} />}
          />
          <StatCard
            label="Gen"
            value={`Gen ${vm.generation}`}
            icon={<Hash size={12} />}
            sub={rmmSystem?.model ?? undefined}
          />
        </div>

        {/* Skeleton while loading */}
        {isLoadingRmm && <RmmSkeleton />}

        {/* RMM sections */}
        {!isLoadingRmm && rmm && (
          <>
            {rmmOs && (
              <SectionCard
                title="Operating System"
                icon={<Terminal size={13} />}
              >
                <KVRow
                  label="Name"
                  value={rmmOs.name}
                  icon={<Layers size={12} />}
                />
                <KVRow
                  label="Architecture"
                  value={rmmOs.architecture}
                  icon={<Cpu size={12} />}
                />
                {rmmOs.buildNumber && (
                  <KVRow
                    label="Build"
                    value={rmmOs.buildNumber}
                    icon={<Hash size={12} />}
                  />
                )}
                <KVRow
                  label="Last boot"
                  value={DateUtil.formatUnix(rmmOs.lastBootTime)}
                  icon={<Clock size={12} />}
                />
                <KVRow
                  label="Language"
                  value={rmmOs.language ?? rmmOs.locale}
                  icon={<Globe size={12} />}
                />
                <KVRow
                  label="Reboot needed"
                  icon={<RefreshCw size={12} />}
                  value={
                    rmmOs.needsReboot ? (
                      <Tag color="warning">
                        <AlertTriangle size={10} className="inline mr-1" />
                        Yes
                      </Tag>
                    ) : (
                      <Tag color="success">
                        <CheckCircle size={10} className="inline mr-1" />
                        No
                      </Tag>
                    )
                  }
                />
              </SectionCard>
            )}

            {rmmProcessors.length > 0 && (
              <SectionCard title="Compute" icon={<Cpu size={13} />}>
                <KVRow
                  label="Model"
                  value={rmmProcessors[0].name}
                  icon={<Layers size={12} />}
                />
                <KVRow
                  label="Physical cores"
                  value={rmmProcessors[0].numCores}
                  icon={<Cpu size={12} />}
                />
                <KVRow
                  label="Logical cores"
                  value={rmmProcessors[0].numLogicalCores}
                  icon={<Zap size={12} />}
                />
                {rmmProcessors[0].maxClockSpeed && (
                  <KVRow
                    label="Speed"
                    value={`${(rmmProcessors[0].maxClockSpeed / 1_000_000_000).toFixed(2)} GHz`}
                    icon={<Activity size={12} />}
                  />
                )}
                <KVRow
                  label="Memory"
                  value={`${Math.round(vm.memoryMB / 1024)} GB`}
                  icon={<Database size={12} />}
                />
              </SectionCard>
            )}

            {rmmVolumes.length > 0 && (
              <SectionCard title="Storage" icon={<HardDrive size={13} />}>
                <div className="flex flex-wrap gap-2 pt-1">
                  <ProductStorage volumes={rmmVolumes} />
                </div>
              </SectionCard>
            )}

            {(rmmIPs.length > 0 || rmmMacs.length > 0 || rmm.publicIP) && (
              <SectionCard title="Network" icon={<Wifi size={13} />}>
                {rmm.publicIP && (
                  <KVRow
                    label="Public IP"
                    value={rmm.publicIP}
                    icon={<Globe size={12} />}
                  />
                )}
                {rmmMacs[0] && (
                  <KVRow
                    label="MAC"
                    value={rmmMacs[0]}
                    icon={<Layers size={12} />}
                  />
                )}
                {rmmIPs.length > 0 && (
                  <>
                    <Divider className="my-2 opacity-20" />
                    <div className="text-xs text-slate-500 mb-1.5">
                      IP Addresses
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {rmmIPs.map((ip: string) => (
                        <IpChip key={ip} ip={ip} />
                      ))}
                    </div>
                  </>
                )}
              </SectionCard>
            )}

            {rmmSystem && (
              <SectionCard
                title="System"
                icon={<Server size={13} />}
                colapse={true}
              >
                {rmmSystem.manufacturer && (
                  <KVRow
                    label="Manufacturer"
                    value={rmmSystem.manufacturer}
                    icon={<Layers size={12} />}
                  />
                )}
                {rmmSystem.model && (
                  <KVRow
                    label="Model"
                    value={rmmSystem.model}
                    icon={<Monitor size={12} />}
                  />
                )}
                {rmmSystem.domain && (
                  <KVRow
                    label="Domain"
                    value={rmmSystem.domain}
                    icon={<Globe size={12} />}
                  />
                )}
                {rmmSystem.domainRole && (
                  <KVRow
                    label="Role"
                    value={rmmSystem.domainRole}
                    icon={<Shield size={12} />}
                  />
                )}
                {rmm.lastLoggedInUser && (
                  <KVRow
                    label="Last user"
                    value={rmm.lastLoggedInUser}
                    icon={<User size={12} />}
                  />
                )}
              </SectionCard>
            )}
            {rmmApps && (
              <SectionCard
                title="System Apps"
                icon={<Package size={13} />}
                colapse={true}
              >
                <div className="space-y-2">
                  <ListComponent
                    data={rmmApps}
                    renderItem={(group: {
                      publisher: string;
                      count: number;
                      apps: RmmApp[];
                    }) => (
                      <Panel bordered>
                        {/* HEADER */}
                        <div className="flex items-center justify-between px-3 py-2 bg-base-200 rounded-t-xl">
                          <div className="flex items-center gap-2 font-medium text-sm">
                            <span>{group.publisher}</span>
                          </div>

                          <div className="text-xs opacity-60">
                            {group.count} apps
                          </div>
                        </div>

                        {/* BODY */}
                        <div className="px-3 py-2">
                          <ListComponent
                            data={group.apps}
                            renderItem={(item: RmmApp) => (
                              <div className="flex justify-between text-xs py-1">
                                <span className="truncate">{item.name}</span>
                                <span className="opacity-50">
                                  {item.version}
                                </span>
                              </div>
                            )}
                          />
                        </div>
                      </Panel>
                    )}
                  />
                </div>
              </SectionCard>
            )}

            <SectionCard
              title="RMM Activity"
              icon={<Activity size={13} />}
              colapse={true}
            >
              <KVRow
                label="Last contact"
                value={DateUtil.formatUnix(rmm.lastContact)}
                icon={<Clock size={12} />}
              />
              <KVRow
                label="Last update"
                value={DateUtil.formatUnix(rmm.lastUpdate)}
                icon={<RefreshCw size={12} />}
              />
              {rmm.approvalStatus && (
                <KVRow
                  label="Approval"
                  icon={<CheckCircle size={12} />}
                  value={
                    <Tag
                      color={
                        rmm.approvalStatus === 'APPROVED'
                          ? 'success'
                          : 'warning'
                      }
                    >
                      {rmm.approvalStatus}
                    </Tag>
                  }
                />
              )}
            </SectionCard>
          </>
        )}

        {/* No RMM fallback notice */}
        {!isLoadingRmm && !rmm && serial && (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 text-xs text-slate-400">
            <AlertCircle
              size={14}
              className="flex-shrink-0 text-slate-300 dark:text-slate-600"
            />
            No RMM record found for serial{' '}
            <span className="font-mono ml-0.5">{serial}</span>
          </div>
        )}

        {/* Fallback compute when no RMM */}
        {!isLoadingRmm && !rmm && (
          <SectionCard title="Compute" icon={<Cpu size={13} />}>
            <KVRow
              label="vCPU count"
              value={vm.cpuCount}
              icon={<Cpu size={12} />}
            />
            <KVRow
              label="Memory"
              value={`${Math.round(vm.memoryMB / 1024)} GB`}
              icon={<Database size={12} />}
            />
            <KVRow
              label="Generation"
              value={`Gen ${vm.generation}`}
              icon={<Hash size={12} />}
            />
          </SectionCard>
        )}

        {/* Identifiers */}
        {/* <SectionCard title="Identifiers" icon={<Hash size={13} />}>
          <div className="text-xs text-slate-500 mb-1">Serial Number</div>
          <MonoBlock value={serial || 'N/A'} />
          <div className="text-xs text-slate-500 mb-1 mt-3">VM ID</div>
          <MonoBlock value={vm.id} />
          {rmmSystem?.biosSerialNumber && (
            <>
              <div className="text-xs text-slate-500 mb-1 mt-3">
                BIOS Serial
              </div>
              <MonoBlock value={rmmSystem.biosSerialNumber} />
            </>
          )}
        </SectionCard> */}
      </div>
    );
  }

  return (
    <div className="py-4 text-sm text-slate-500">Unsupported node type</div>
  );
};

export default ServerDetailContainer;
